import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LuCheck, LuCopy } from 'react-icons/lu';

// Building blocks of the audit event drawer.

export const Section = ({ title, icon: Icon, children }) => (
  <section className="tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 tw-bg-white">
    <h4 className="tw-flex tw-items-center tw-gap-2 tw-m-0 tw-px-4 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 tw-border-0 tw-border-b tw-border-solid tw-border-slate-100">
      {Icon && <Icon size={13} aria-hidden="true" />}
      {title}
    </h4>
    <div className="tw-px-4 tw-py-1">{children}</div>
  </section>
);

Section.propTypes = { title: PropTypes.string.isRequired, icon: PropTypes.elementType, children: PropTypes.node.isRequired };
Section.defaultProps = { icon: null };

// One labelled line: icon, label, value. Empty values show a muted dash.
export const DetailRow = ({ icon: Icon, label, children }) => {
  const empty = children === null || children === undefined || children === '';
  return (
    <div className="tw-flex tw-items-start tw-gap-3 tw-py-2.5 tw-border-0 tw-border-b tw-border-solid tw-border-slate-100 last:tw-border-b-0">
      <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-md tw-bg-slate-50 tw-text-slate-500 tw-shrink-0">
        <Icon size={14} aria-hidden="true" />
      </span>
      <div className="tw-min-w-0 tw-flex-1">
        <div className="tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-400">{label}</div>
        <div className="tw-text-sm tw-text-slate-800 tw-break-words">
          {empty ? <span className="tw-text-slate-400">—</span> : children}
        </div>
      </div>
    </div>
  );
};

DetailRow.propTypes = { icon: PropTypes.elementType.isRequired, label: PropTypes.string.isRequired, children: PropTypes.node };
DetailRow.defaultProps = { children: null };

// Monospace value with a copy button (request id, record id).
export const CopyValue = ({ value }) => {
  const [copied, setCopied] = useState(false);

  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-max-w-full">
      <code className="tw-font-mono tw-text-xs tw-text-slate-700 tw-bg-slate-50 tw-px-1.5 tw-py-0.5 tw-rounded tw-truncate">{value}</code>
      <button
        type="button"
        onClick={copy}
        className="tw-inline-flex tw-items-center tw-p-1 tw-rounded tw-text-slate-400 hover:tw-text-slate-700 hover:tw-bg-slate-100 tw-bg-transparent tw-border-0 tw-cursor-pointer"
        aria-label={copied ? 'Copied' : 'Copy'}
        title={copied ? 'Copied' : 'Copy'}
      >
        {copied ? <LuCheck size={13} className="tw-text-emerald-600" /> : <LuCopy size={13} />}
      </button>
    </span>
  );
};

CopyValue.propTypes = { value: PropTypes.string.isRequired };
